import { type ReactNode } from 'react';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { isWeb, YStack, type YStackProps } from 'tamagui';
import { LinearGradient } from 'tamagui/linear-gradient';

import { BodyMediumRegular } from '../text/Typography';

const SCROLL_GRADIENT_HEIGHT = 60;

export const WEB_SHEET_MIN_HEIGHT = 200;

export const getMaxSheetHeight = (screenHeight: number, topInset: number) => {
    if (!isWeb) {
        return (screenHeight - topInset) * 0.95;
    }
    if (screenHeight > 1200) {
        return 1000;
    }
    if (screenHeight > 1000) {
        return screenHeight * 0.75;
    }
    return screenHeight * 0.93;
};

export const getPaddingForScrollGradient = (
    shouldShowScrollGradient: boolean,
    bottomInset: number,
) => {
    if (shouldShowScrollGradient) {
        return SCROLL_GRADIENT_HEIGHT * 0.25 + bottomInset;
    }
    return 0;
};

export interface SheetContentProps extends YStackProps {
    children?: ReactNode;
    withPadding?: boolean;
    shouldShowScrollGradient?: boolean;
}

export const SheetContent = ({
    children,
    withPadding = true,
    shouldShowScrollGradient = false,
    ...props
}: SheetContentProps) => {
    const hasChildren = children != null;
    const { bottom: bottomInset } = useSafeAreaInsets();
    const bottom = shouldShowScrollGradient ? 0 : bottomInset;

    return (
        <YStack
            flex={1}
            paddingHorizontal={withPadding ? 24 : 0}
            paddingBottom={withPadding ? Math.max(bottom, 24) : 0}
            gap={16}
            width="100%"
            alignItems="stretch"
            {...props}
        >
            {hasChildren &&
                (typeof children === 'string' ? (
                    <BodyMediumRegular color="$gray1000">
                        {children}
                    </BodyMediumRegular>
                ) : (
                    children
                ))}
        </YStack>
    );
};

export const SheetScrollGradient = () => {
    const { bottom } = useSafeAreaInsets();
    return (
        <LinearGradient
            colors={['rgba(255, 255, 255, 0)', 'rgba(255, 255, 255, 1)']}
            start={[0, 0]}
            end={[0, 1]}
            position="absolute"
            bottom={0}
            left={0}
            right={0}
            height={SCROLL_GRADIENT_HEIGHT + bottom}
            pointerEvents="none"
        />
    );
};
